<script lang="ts">
  import { api } from '$shared/api';
  import { addToast } from '$shared/toasts.svelte';
  import { getCurrentRoute } from '$lib/router.svelte';
  import { isLoggedIn } from '$lib/auth.svelte';
  import { handleShareCard, getShareUrl } from '$lib/utils/share';
  import { trackEvent } from '$lib/analytics.svelte';
  import { phaseEnd, phaseLabel } from '$lib/utils/duel-phase';
  import { subscribeDuelRoom, type DuelEvent } from '$lib/stores/socket.svelte';
  import LoadingSkeleton from '$shared/LoadingSkeleton.svelte';
  import Avatar from '$shared/Avatar.svelte';
  import Countdown from '$components/shared/Countdown.svelte';
  import DuelStatus from '$components/shared/DuelStatus.svelte';
  import VoteModeChip from '$components/shared/VoteModeChip.svelte';
  import AppCta from '$components/shared/AppCta.svelte';
  import PlayerEmbed from '$components/shared/PlayerEmbed.svelte';
  import type { Duel, DuelEntry } from '$types/index';

  let { id }: { id: string } = $props();
  const duelId = $derived(Number(id));

  /** GET /duels/{id} — the list shape + my-state + embedded anonymized entries. */
  type DuelView = Duel & { format?: string; entries?: DuelEntry[] };

  let loading = $state(true);
  let error = $state(false);
  let duel = $state<DuelView | null>(null);
  let entries = $state<DuelEntry[]>([]);
  let expandedTrackId = $state<number | null>(null);
  let autoExpandChecked = false;

  const phase = $derived(duel?.status ?? '');
  const isLive = $derived(phase === 'open' || phase === 'voting');
  const showPrompt = $derived(
    duel != null && (duel.theme_type === 'prompt' || duel.theme_type === 'global') && !!duel.prompt_text,
  );
  const themeMeta = $derived(
    duel
      ? [duel.genre_name, duel.style].filter(Boolean).join(' · ') || (duel.theme_type === 'global' ? 'Global' : '')
      : '',
  );
  const countdownEnd = $derived(duel ? phaseEnd(duel) : null);
  const entryPct = $derived(
    duel && duel.entry_cap > 0 ? Math.min(100, Math.round(((duel.entry_count ?? 0) / duel.entry_cap) * 100)) : 0,
  );

  // Live tallies MAY be absent during voting — only render counts when the
  // server actually sent them.
  const hasTallies = $derived(phase === 'voting' && entries.some((e) => e.vote_count != null));
  const maxVotes = $derived(entries.reduce((max, e) => Math.max(max, e.vote_count ?? 0), 0));

  function fetchData() {
    return api
      .get<DuelView>(`/duels/${id}`)
      .then((d) => {
        duel = d;
        entries = Array.isArray(d.entries) ? d.entries : [];
        loading = false;

        // `?track={id}` deep-link — auto-expand that entry's player once, on first load.
        if (!autoExpandChecked) {
          autoExpandChecked = true;
          const wanted = Number(new URLSearchParams(getCurrentRoute().query).get('track'));
          if (wanted && entries.some((e) => e.track_id === wanted)) {
            expandedTrackId = wanted;
          }
        }
      })
      .catch((e) => {
        console.error('Failed to load duel:', e);
        error = true;
        loading = false;
      });
  }

  /**
   * Duel-room events, handled tolerantly (fields may be absent):
   * - `entry_claimed` → a new anonymous claim landed; refresh the detail.
   * - `vote` with {track_id, vote_count} → update that entry's tally in place.
   */
  function handleDuelEvent(event: DuelEvent) {
    if (event.type === 'entry_claimed') {
      fetchData();
      return;
    }
    if (event.track_id == null || event.vote_count == null) return;
    if (!entries.some((e) => e.track_id === event.track_id)) return;
    entries = entries.map((e) => (e.track_id === event.track_id ? { ...e, vote_count: event.vote_count } : e));
  }

  $effect(() => {
    fetchData();
    const unsubscribe = subscribeDuelRoom(duelId, handleDuelEvent);
    return unsubscribe;
  });

  function toggleExpand(trackId: number) {
    expandedTrackId = expandedTrackId === trackId ? null : trackId;
  }

  function thumbUrl(entry: DuelEntry): string | null {
    return entry.youtube_id ? `https://img.youtube.com/vi/${entry.youtube_id}/default.jpg` : null;
  }

  /** Append UTM params for platform attribution. Reject non-http(s) schemes. */
  function platformUrl(url: string): string | null {
    try {
      const u = new URL(url);
      // Reject non-http(s) schemes to prevent javascript: and data: injection
      if (u.protocol !== 'https:' && u.protocol !== 'http:') {
        return null;
      }
      u.searchParams.set('utm_source', 'beatduel');
      u.searchParams.set('utm_medium', 'duel');
      return u.toString();
    } catch {
      return null;
    }
  }

  function externalUrlFor(entry: DuelEntry): string | null {
    return entry.youtube_id ? platformUrl(`https://www.youtube.com/watch?v=${entry.youtube_id}`) : null;
  }

  function handleOutboundClick(entry: DuelEntry) {
    trackEvent('outbound_click', {
      target_id: entry.track_id,
      target_type: 'track',
      metadata: { platform: 'youtube', duel_id: duelId },
    });
  }

  function shareText(): string {
    const name = duel?.name ?? `Duel #${id}`;
    return phase === 'ended'
      ? `Check out the final results for ${name} on Beat Duel!`
      : `Check out ${name} on Beat Duel — listen to the entries before the reveal!`;
  }

  function copyLink() {
    navigator.clipboard.writeText(getShareUrl(`duels/${id}`));
    addToast('Link copied!', 'join');
  }
</script>

<div class="duel-detail">
  {#if loading}
    <LoadingSkeleton lines={6} />
  {:else if error || !duel}
    <p class="empty">Duel not found — it may have been removed, or the link is incorrect.</p>
    <div class="back"><a href="#/duels">&larr; Back to Duels</a></div>
  {:else}
    <header class="duel-header">
      <div class="duel-header__top">
        <h1>{duel.name || `Duel #${id}`}</h1>
        <DuelStatus status={duel.status} />
        <VoteModeChip mode={duel.vote_mode} />
      </div>
      {#if showPrompt}
        <blockquote class="duel-header__prompt">{duel.prompt_text}</blockquote>
      {/if}
      {#if themeMeta}
        <p class="duel-header__meta">{themeMeta}</p>
      {/if}
      <div class="duel-header__stats">
        <span class="entry-meter">
          <span class="entry-meter__count"
            >{duel.entry_count ?? 0}{duel.entry_cap ? `/${duel.entry_cap}` : ''} entries</span
          >
          {#if duel.entry_cap > 0}
            <span class="entry-meter__bar"><span class="entry-meter__fill" style="width:{entryPct}%"></span></span>
          {/if}
        </span>
        {#if countdownEnd}
          <span class="countdown">{phaseLabel(duel)} in <Countdown end={countdownEnd} /></span>
        {/if}
      </div>
    </header>

    {#if phase === 'ended'}
      <a href="#/duels/{id}/result" class="hero-cta hero-cta--results">
        <span class="hero-cta__label">See the reveal — crowns &amp; final standings</span>
        <span class="hero-cta__arrow" aria-hidden="true">&rarr;</span>
      </a>
    {:else if isLive}
      <div class="anon-banner" role="note">Entries are anonymous until the duel ends</div>
      <AppCta
        duelId={duel.id}
        label={phase === 'open' ? 'Claim a song into this duel in the app' : 'Vote in the app'}
      />
    {/if}

    {#if entries.length === 0}
      <p class="empty">No entries yet — claims are made in the app.</p>
    {:else}
      <div class="entry-list">
        {#each entries as entry (entry.entry_id)}
          <div class="entry-row" class:entry-row--mine={entry.is_mine}>
            <div class="entry-row__main">
              {#if thumbUrl(entry)}
                <img src={thumbUrl(entry)} alt="" class="entry-thumb" loading="lazy" />
              {/if}
              <div class="entry-info">
                <span class="entry-title">{entry.title || 'Untitled'}</span>
                <span class="entry-by">
                  {#if entry.curator}
                    <!-- Curator revealed (ended) or the viewer's own entry -->
                    <a href="#/users/{entry.curator.username}" class="entry-curator">
                      <Avatar username={entry.curator.username} avatarUrl={entry.curator.avatar_url} size="xs" />
                      {entry.curator.username}
                    </a>
                  {/if}
                  {#if entry.creator_name}
                    <span class="entry-creator">{entry.curator ? '· ' : ''}by {entry.creator_name}</span>
                  {/if}
                  {#if entry.is_mine}
                    <span class="badge badge--green entry-mine-badge">Your entry</span>
                  {/if}
                </span>
              </div>
              {#if hasTallies && entry.vote_count != null}
                <div class="entry-votes">
                  <span class="vote-num">{entry.vote_count}</span>
                  <span class="vote-label">votes</span>
                </div>
                <div class="vote-bar">
                  <div
                    class="vote-bar-fill"
                    style="width:{maxVotes > 0 ? Math.round(((entry.vote_count ?? 0) / maxVotes) * 100) : 0}%"
                  ></div>
                </div>
              {/if}
              <button
                type="button"
                class="listen-toggle"
                aria-expanded={expandedTrackId === entry.track_id}
                onclick={() => toggleExpand(entry.track_id)}
              >
                {expandedTrackId === entry.track_id ? 'Hide' : 'Listen'}
                <span class="listen-toggle__chevron" aria-hidden="true"
                  >{expandedTrackId === entry.track_id ? '▲' : '▼'}</span
                >
              </button>
            </div>

            {#if expandedTrackId === entry.track_id}
              <div class="entry-expanded">
                <PlayerEmbed trackType="youtube" youtubeId={entry.youtube_id} />
                {#if externalUrlFor(entry)}
                  <a
                    href={externalUrlFor(entry)}
                    target="_blank"
                    rel="noopener"
                    class="platform-link"
                    onclick={() => handleOutboundClick(entry)}
                  >
                    View on YouTube
                  </a>
                {/if}
              </div>
            {/if}
          </div>
        {/each}
      </div>
    {/if}

    <div class="duel-actions">
      {#if isLoggedIn()}
        <button
          type="button"
          class="btn btn-outline btn-sm"
          onclick={() => handleShareCard(duelId, shareText(), phase === 'ended' ? 'rank' : 'countdown')}
        >
          Share Image
        </button>
      {/if}
      <button type="button" class="btn btn-outline btn-sm" onclick={copyLink}>Copy Link</button>
    </div>
    <div class="back"><a href="#/duels">&larr; Back to Duels</a></div>
  {/if}
</div>

<style>
  .duel-detail {
    padding-bottom: 2rem;
  }

  /* Header */
  .duel-header {
    margin-bottom: 1.25rem;
  }
  .duel-header__top {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
  }
  .duel-header__top h1 {
    margin: 0;
  }
  .duel-header__prompt {
    margin: 0.75rem 0 0;
    padding: 0.5rem 0.9rem;
    border-left: 3px solid var(--purple);
    font-style: italic;
    font-size: 1rem;
    color: var(--text-secondary);
  }
  .duel-header__meta {
    margin: 0.5rem 0 0;
    font-size: 0.875rem;
    color: var(--text-secondary);
  }
  .duel-header__stats {
    margin-top: 0.625rem;
    display: flex;
    align-items: center;
    gap: 1.25rem;
    font-size: 0.8125rem;
    color: var(--text-muted);
    flex-wrap: wrap;
  }
  .entry-meter {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
  }
  .entry-meter__bar {
    display: inline-block;
    width: 5rem;
    height: 0.375rem;
    background: var(--surface-2);
    border-radius: 4px;
    overflow: hidden;
  }
  .entry-meter__fill {
    display: block;
    height: 100%;
    background: var(--cyan);
    border-radius: 4px;
  }
  .countdown {
    color: var(--cyan);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  /* Hero CTA — echoes the share-card's top gradient stripe as this page's signature accent */
  .hero-cta {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1.5rem;
    padding: 1.1rem 1.35rem;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius-lg);
    overflow: hidden;
  }
  .hero-cta::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(135deg, var(--cyan) 0%, var(--purple) 100%);
  }
  .hero-cta--results:hover {
    border-color: rgba(0, 229, 255, 0.35);
  }
  .hero-cta__label {
    font-weight: 700;
    font-size: 1.05rem;
    color: var(--cyan);
  }
  .hero-cta__arrow {
    font-size: 1.25rem;
    color: var(--cyan);
    flex-shrink: 0;
  }

  /* Anonymity banner */
  .anon-banner {
    margin-bottom: 1rem;
    padding: 0.6rem 0.9rem;
    font-size: 0.8125rem;
    color: var(--text-secondary);
    background: rgba(147, 51, 234, 0.08);
    border: 1px solid rgba(147, 51, 234, 0.3);
    border-radius: var(--radius);
  }

  .empty {
    color: var(--text-secondary);
  }

  /* Entry list */
  .entry-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  .entry-row {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    overflow: hidden;
  }
  .entry-row--mine {
    border-color: rgba(57, 255, 20, 0.3);
  }
  .entry-row__main {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
  }
  .entry-thumb {
    width: 48px;
    height: 36px;
    object-fit: cover;
    border-radius: 4px;
    flex-shrink: 0;
  }
  .entry-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
  .entry-title {
    font-weight: 600;
    color: var(--text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .entry-by {
    font-size: 0.875rem;
    color: var(--text-secondary);
    margin-top: 0.125rem;
    display: flex;
    align-items: center;
    gap: 0.35rem;
    flex-wrap: wrap;
  }
  .entry-curator {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    color: var(--text-secondary);
  }
  .entry-curator:hover {
    color: var(--text);
  }
  .entry-creator {
    color: var(--text-muted);
    font-size: 0.8rem;
  }
  .entry-mine-badge {
    font-size: 0.65rem;
  }
  .entry-votes {
    text-align: right;
    flex-shrink: 0;
  }
  .vote-num {
    font-size: 1.125rem;
    font-weight: 700;
    color: var(--cyan);
  }
  .vote-label {
    display: block;
    font-size: 0.7rem;
    color: var(--text-muted);
  }
  .vote-bar {
    width: 6rem;
    height: 0.5rem;
    background: var(--bg);
    border-radius: 4px;
    display: none;
    flex-shrink: 0;
  }
  .vote-bar-fill {
    height: 100%;
    background: var(--cyan);
    border-radius: 4px;
  }
  @media (min-width: 640px) {
    .vote-bar {
      display: block;
    }
  }

  .listen-toggle {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0.4rem 0.75rem;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--cyan);
    background: rgba(0, 229, 255, 0.08);
    border: 1px solid rgba(0, 229, 255, 0.35);
    border-radius: 999px;
    cursor: pointer;
  }
  .listen-toggle:hover {
    background: rgba(0, 229, 255, 0.16);
  }
  .listen-toggle__chevron {
    font-size: 0.6rem;
  }

  .entry-expanded {
    padding: 0 1rem 1rem;
    border-top: 1px solid var(--border);
    padding-top: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
  }
  .platform-link {
    align-self: flex-start;
    font-size: 0.8125rem;
    color: var(--text-muted);
  }
  .platform-link:hover {
    color: var(--cyan);
  }

  .duel-actions {
    margin-top: 1.5rem;
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
  }
  .back {
    margin-top: 1.5rem;
  }
  .back a {
    font-size: 0.875rem;
    color: var(--cyan);
  }
</style>
